import { osnovaApi } from '../base';
import type { AppDispatch } from '@app/store';
import type { FleetGroup, FleetGroupData, FleetGroupDetail, FleetGroupSeedResult } from '../types';

/**
 * Группы механизаторов справочника техники (`/api/v1/fleet/groups`, ТЗ
 * AI-Economist-2026 ред. 2.1, п. 6.3.1, 7.1). Читать может каждый, кто видит
 * справочник; править — право записи в Экономиста.
 *
 * Теги: `{FleetGroup, 'LIST'}` — список, `{FleetGroup, <id>}` — карточка.
 * Правка одной группы меняет и другие (модель переносится из чужой группы),
 * поэтому мутации сбрасывают весь тип `FleetGroup`, ставки на дату и список
 * моделей справочника — в нём у модели её группа. Счётчики и ПРЦ моделей
 * группы зависят от справочника — запросы групп подписаны и на
 * `{Fleet, 'LIST'}`: архив модели, импорт книги ЭММ.
 */

// `as const` — литералы типов вместо string: иначе RTK не узнает теги из tagTypes.
const LIST = { type: 'FleetGroup', id: 'LIST' } as const;
const FLEET_LIST = { type: 'Fleet', id: 'LIST' } as const;
const RATES = { type: 'EconGroupRate', id: 'LIST' } as const;

export const fleetGroupsApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Группы; архивные — только по `include_archived`. */
    listFleetGroups: b.query<{ items: FleetGroup[] }, { include_archived?: boolean }>({
      query: (params) => ({ url: '/fleet/groups', params }),
      providesTags: [LIST, FLEET_LIST],
    }),

    /** Карточка: группа и модели с ПРЦ, действующей сегодня. */
    getFleetGroup: b.query<FleetGroupDetail, string>({
      query: (id) => `/fleet/groups/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'FleetGroup', id }, FLEET_LIST],
    }),

    /** Новая группа; 409 — группа с таким названием уже есть. */
    createFleetGroup: b.mutation<FleetGroupDetail, FleetGroupData>({
      query: (body) => ({ url: '/fleet/groups', method: 'POST', body }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeGroup(dispatch, queryFulfilled),
      invalidatesTags: ['FleetGroup', RATES, FLEET_LIST],
    }),

    /**
     * Правка группы. `expected_updated_at` — `updated_at` открытой карточки:
     * если группу успели изменить (в том числе забрали из неё модель), backend
     * отвечает 409 и ничего не пишет. Тем же 409 приходят «название уже
     * занято» и «должность уже в другой группе».
     */
    updateFleetGroup: b.mutation<
      FleetGroupDetail,
      { id: string; data: FleetGroupData; expected_updated_at: string }
    >({
      query: ({ id, data, expected_updated_at }) => ({
        url: `/fleet/groups/${id}`,
        method: 'PUT',
        body: { ...data, expected_updated_at },
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeGroup(dispatch, queryFulfilled),
      invalidatesTags: ['FleetGroup', RATES, FLEET_LIST],
    }),

    /** В архив: модели и должности группы освобождаются, ставки остаются в истории. */
    archiveFleetGroup: b.mutation<FleetGroupDetail, string>({
      query: (id) => ({ url: `/fleet/groups/${id}/archive`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeGroup(dispatch, queryFulfilled),
      invalidatesTags: ['FleetGroup', RATES, FLEET_LIST],
    }),

    /** Вернуть из архива — без моделей и должностей; 409 — название уже занято. */
    restoreFleetGroup: b.mutation<FleetGroupDetail, string>({
      query: (id) => ({ url: `/fleet/groups/${id}/restore`, method: 'POST' }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => storeGroup(dispatch, queryFulfilled),
      invalidatesTags: ['FleetGroup', RATES, FLEET_LIST],
    }),

    /**
     * Подобрать группы по должности механизатора паспорта: новые группы с
     * должностью Хронума и свободные модели в работе и резерве. Правки
     * экономиста не трогаются.
     */
    seedFleetGroups: b.mutation<{ result: FleetGroupSeedResult; items: FleetGroup[] }, void>({
      query: () => ({ url: '/fleet/groups/seed', method: 'POST' }),
      invalidatesTags: ['FleetGroup', RATES, FLEET_LIST],
    }),
  }),
  overrideExisting: false,
});

/** Ответ мутации — сразу в кеш карточки: экран не ждёт повторного GET. */
async function storeGroup(
  dispatch: AppDispatch,
  queryFulfilled: Promise<{ data: FleetGroupDetail }>,
): Promise<void> {
  try {
    const { data } = await queryFulfilled;
    void dispatch(fleetGroupsApi.util.upsertQueryData('getFleetGroup', data.group.id, data));
  } catch {
    /* ошибку показывает экран через unwrap() */
  }
}

export const {
  useListFleetGroupsQuery,
  useGetFleetGroupQuery,
  useLazyGetFleetGroupQuery,
  useCreateFleetGroupMutation,
  useUpdateFleetGroupMutation,
  useArchiveFleetGroupMutation,
  useRestoreFleetGroupMutation,
  useSeedFleetGroupsMutation,
} = fleetGroupsApi;
